import {
  ObservationFulfillmentControllerService,
  ObservationFulfillmentItem
} from "@9amhealth/openapi";
import { Cubit } from "blac-next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { TranslationKey } from "src/types/translationKey";

export interface GroupedCategory {
  category: ObservationFulfillmentItem.category;
  displayName: string;
  items: ObservationFulfillmentItem[];
  requiredCompleted: boolean | null;
  hasRequired: boolean;
  hasCompleted: boolean;
  allCompleted: boolean;
}

export interface LabStatusOverviewState {
  loading: boolean;
  error: TranslationKey | null;
  labData: ObservationFulfillmentItem[] | null;
  groupedCategories: GroupedCategory[];
  mockData: ObservationFulfillmentItem[] | null;
}

const initialState: LabStatusOverviewState = {
  loading: false,
  error: null,
  labData: null,
  groupedCategories: [],
  mockData: null
};

export default class LabStatusOverviewCubit extends Cubit<LabStatusOverviewState> {
  static keepAlive = true;
  constructor() {
    super(initialState);
  }

  private categoryOrder: ObservationFulfillmentItem.category[] = [
    ObservationFulfillmentItem.category.CARDIOMETABOLIC_HEALTH,
    ObservationFulfillmentItem.category.KIDNEY_HEALTH,
    ObservationFulfillmentItem.category.LIVER_HEALTH
  ];

  private getCategoryDisplayName(
    category: ObservationFulfillmentItem.category
  ): string {
    switch (category) {
      case ObservationFulfillmentItem.category.CARDIOMETABOLIC_HEALTH:
        return translate("labCategory.cardiometabolicHealth");
      case ObservationFulfillmentItem.category.KIDNEY_HEALTH:
        return translate("labCategory.kidneyHealth");
      case ObservationFulfillmentItem.category.LIVER_HEALTH:
        return translate("labCategory.liverHealth");
      default:
        return category;
    }
  }

  private groupItemsByCategory(
    items: ObservationFulfillmentItem[]
  ): GroupedCategory[] {
    const categoryMap = new Map<
      ObservationFulfillmentItem.category,
      ObservationFulfillmentItem[]
    >();

    // Group items by category
    items.forEach((item) => {
      if (!categoryMap.has(item.category)) {
        categoryMap.set(item.category, []);
      }
      const categoryItems = categoryMap.get(item.category);
      if (categoryItems) {
        categoryItems.push(item);
      }
    });

    // Convert to GroupedCategory objects
    return Array.from(categoryMap.entries())
      .map(([category, categoryItems]) => {
        const requiredItems = categoryItems.filter((item) => item.isRequired);
        const hasRequired = requiredItems.length > 0;
        const completedItems = categoryItems.filter(
          (item) => item.status === "COMPLETE"
        );
        const hasCompleted = completedItems.length > 0;
        const allCompleted =
          categoryItems.length > 0 &&
          categoryItems.every((item) => item.status === "COMPLETE");
        const requiredCompleted = hasRequired
          ? categoryItems.filter(
              (item) => item.isRequired && item.status === "COMPLETE"
            ).length === requiredItems.length
          : null;

        return {
          category,
          displayName: this.getCategoryDisplayName(category),
          items: categoryItems,
          hasRequired,
          requiredCompleted,
          hasCompleted,
          allCompleted
        } satisfies GroupedCategory;
      })
      .sort((a, b) => {
        const indexA = this.categoryOrder.indexOf(a.category);
        const indexB = this.categoryOrder.indexOf(b.category);
        if (indexA === -1 && indexB === -1) {
          return a.displayName.localeCompare(b.displayName);
        }
        if (indexA === -1) {
          return 1;
        }
        if (indexB === -1) {
          return -1;
        }
        return indexA - indexB;
      });
  }

  setMockData = (data: ObservationFulfillmentItem[] | null): void => {
    this.patch({
      mockData: data,
      labData: null,
      groupedCategories: []
    });
  };

  clearMockData = (): void => {
    this.patch({
      mockData: null,
      labData: null,
      groupedCategories: []
    });
  };

  fetchLabStatus = async (): Promise<void> => {
    const hasLabData = this.state.labData !== null;
    if (this.state.loading || hasLabData) {
      return;
    }

    this.patch({
      loading: true,
      error: null
    });

    try {
      let labData: ObservationFulfillmentItem[];

      if (this.state.mockData) {
        labData = this.state.mockData;
      } else {
        const response =
          await ObservationFulfillmentControllerService.onboardingFulfillmentStatus();

        labData = response.data.observationFulfillmentItems;
      }

      const groupedCategories = this.groupItemsByCategory(labData);

      groupedCategories.forEach((category) => {
        category.items.sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        );
      });

      this.patch({
        loading: false,
        error: null,
        labData,
        groupedCategories
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch lab status";

      reportErrorSentry(
        error instanceof Error ? error : new Error(errorMessage),
        {
          context: "LabStatusOverviewCubit.fetchLabStatus",
          extra: { originalError: error }
        }
      );

      this.patch({
        loading: false,
        error: "error.failedLoadingLabStatus",
        labData: null,
        groupedCategories: []
      });
    }
  };

  get labsTrackingData() {
    const { state } = this;

    return {
      allRequiredLabsCompleted:
        state.labData
          ?.filter((lab) => lab.isRequired)
          .every((lab) => lab.status === "COMPLETE") ?? false,
      completedLabs:
        state.labData
          ?.filter((lab) => lab.status === "COMPLETE")
          .map((lab) => lab.displayName) ?? [],
      missingLabs:
        state.labData
          ?.filter((lab) => lab.status !== "COMPLETE")
          .map((lab) => lab.displayName) ?? [],
      missingRequiredLabs:
        state.labData
          ?.filter((lab) => lab.isRequired && lab.status !== "COMPLETE")
          .map((lab) => lab.displayName) ?? []
    };
  }

  get hasNoLabsCompleted(): boolean {
    const { labData } = this.state;
    if (!labData || labData.length === 0) {
      return true;
    }
    return labData.every((lab) => lab.status !== "COMPLETE");
  }

  get hasIncompleteRequiredLabs(): boolean {
    const { labData } = this.state;
    if (!labData) {
      return false;
    }
    return labData.some((lab) => lab.isRequired && lab.status !== "COMPLETE");
  }

  get requiredLabs(): ObservationFulfillmentItem[] {
    return this.state.labData?.filter((lab) => lab.isRequired) ?? [];
  }

  get allLabs(): ObservationFulfillmentItem[] {
    return this.state.labData ?? [];
  }
}
